package com.ijoin.analytics

import com.ijoin.BuildConfig
import kotlin.js.Date
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.HTMLScriptElement

@JsName("fbq")
private external fun fbqExternal(command: String, param1: dynamic = definedExternally, param2: dynamic = definedExternally)

internal interface AnalyticsProvider {
    fun init()
    fun trackPageView()
    fun trackStandard(event: String, params: Map<String, Any?> = emptyMap())
    fun trackCustom(event: String, params: Map<String, Any?> = emptyMap())
}

/**
 * Web analytics entry point. Dispatches events to all registered providers.
 */
object PixelTracker {
    private val providers: List<AnalyticsProvider> = listOf(
        MetaPixelProvider(),
        GoogleTagProvider()
    )
    private var initialized = false

    fun init() {
        if (!BuildConfig.ANALYTICS_ENABLED) return
        if (initialized) return
        initialized = true
        providers.forEach { it.init() }
    }

    fun trackPageView() {
        if (!BuildConfig.ANALYTICS_ENABLED) return
        ensureInit()
        providers.forEach { it.trackPageView() }
    }

    fun trackStandard(event: String, params: Map<String, Any?> = emptyMap()) {
        if (!BuildConfig.ANALYTICS_ENABLED) return
        ensureInit()
        providers.forEach { it.trackStandard(event, params) }
    }

    fun trackCustom(event: String, params: Map<String, Any?> = emptyMap()) {
        if (!BuildConfig.ANALYTICS_ENABLED) return
        ensureInit()
        providers.forEach { it.trackCustom(event, params) }
    }

    private fun ensureInit() {
        if (!initialized) {
            init()
        }
    }
}

private class MetaPixelProvider : AnalyticsProvider {
    private var initialized = false

    override fun init() {
        if (initialized) return
        initialized = true

        // Inject fbq script (same as Meta snippet)
        val w = window.asDynamic()
        if (w.fbq == null) {
            val d = document
            val script = d.createElement("script") as HTMLScriptElement
            script.async = true
            script.src = "https://connect.facebook.net/en_US/fbevents.js"
            val firstScript = d.getElementsByTagName("script").item(0)
            firstScript?.parentNode?.insertBefore(script, firstScript)

            val fbqStub: dynamic = js(
                """
                (function() {
                  var n = function() {
                    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
                  };
                  n.push = n;
                  n.queue = [];
                  n.loaded = true;
                  n.version = '2.0';
                  return n;
                })();
                """
            )
            w.fbq = fbqStub
            w._fbq = fbqStub
        }

        trackInit()
        trackPageView()
        injectNoScriptPixel()
    }

    override fun trackPageView() {
        track("track", "PageView")
    }

    override fun trackStandard(event: String, params: Map<String, Any?>) {
        if (params.isEmpty()) {
            track("track", event)
        } else {
            track("track", event, params.toJsObject())
        }
    }

    override fun trackCustom(event: String, params: Map<String, Any?>) {
        if (params.isEmpty()) {
            track("trackCustom", event)
        } else {
            track("trackCustom", event, params.toJsObject())
        }
    }

    private fun trackInit() {
        val fbq = window.asDynamic().fbq ?: return
        fbq("init", META_PIXEL_ID)
    }

    private fun track(method: String, arg1: Any? = null, arg2: Any? = null) {
        val fbq = window.asDynamic().fbq ?: return
        when {
            arg2 != null -> fbq(method, arg1, arg2)
            arg1 != null -> fbq(method, arg1)
            else -> fbq(method)
        }
    }

    private fun injectNoScriptPixel() {
        val img = document.createElement("img") as? HTMLImageElement ?: return
        img.height = 1
        img.width = 1
        img.style.display = "none"
        img.src = "https://www.facebook.com/tr?id=$META_PIXEL_ID&ev=PageView&noscript=1"
        document.body?.appendChild(img)
    }
}

private class GoogleTagProvider : AnalyticsProvider {
    private var initialized = false
    private var gtagFn: dynamic = null

    override fun init() {
        if (initialized) return
        initialized = true

        val w = window.asDynamic()
        if (w.dataLayer == null) {
            w.dataLayer = js("[]")
        }
        if (w.gtag == null) {
            val gtagStub: dynamic = js(
                """
                (function() {
                  return function() { window.dataLayer.push(arguments); };
                })();
                """
            )
            w.gtag = gtagStub
        }
        gtagFn = w.gtag

        val d = document
        val script = d.createElement("script") as HTMLScriptElement
        script.async = true
        script.src = "https://www.googletagmanager.com/gtag/js?id=$GOOGLE_TAG_ID"
        val firstScript = d.getElementsByTagName("script").item(0)
        firstScript?.parentNode?.insertBefore(script, firstScript)

        invokeGtag("js", Date())
        invokeGtag("config", GOOGLE_TAG_ID)
    }

    override fun trackPageView() {
        val params = buildMap<String, Any?> {
            put("page_path", window.location.pathname)
            put("page_location", window.location.href)
            val title = document.title
            if (title.isNotBlank()) {
                put("page_title", title)
            }
        }
        trackEvent("page_view", params)
    }

    override fun trackStandard(event: String, params: Map<String, Any?>) {
        trackEvent(event, params)
        trackConversionIfNeeded(event, params)
    }

    override fun trackCustom(event: String, params: Map<String, Any?>) {
        trackEvent(event, params)
        trackConversionIfNeeded(event, params)
    }

    private fun trackEvent(event: String, params: Map<String, Any?>) {
        if (params.isEmpty()) {
            invokeGtag("event", event)
        } else {
            invokeGtag("event", event, params.toJsObject())
        }
    }

    private fun trackConversionIfNeeded(event: String, params: Map<String, Any?>) {
        val sendTo = GOOGLE_ADS_CONVERSIONS[event] ?: return
        val conversionParams = buildMap<String, Any?> {
            put("send_to", sendTo)
            params.forEach { (key, value) ->
                put(key, value)
            }
        }
        invokeGtag("event", "conversion", conversionParams.toJsObject())
    }

    private fun invokeGtag(command: String, param1: Any? = null, param2: Any? = null) {
        val fn = gtagFn ?: return
        when {
            param2 != null -> fn(command, param1, param2)
            param1 != null -> fn(command, param1)
            else -> fn(command)
        }
    }
}

private const val META_PIXEL_ID = "740140332041545"
private const val GOOGLE_TAG_ID = "AW-17896876905"
private val GOOGLE_ADS_CONVERSIONS = mapOf(
    "CompleteRegistration" to "AW-17896876905/y2UPCIHc5eobEOnW8tVC",
    "ViewContent" to "AW-17896876905/jPFQCJrCq-sbEOnW8tVC",
    "HomeFromEvent" to "AW-17896876905/tT_5CK7BrOsbEOnW8tVC",
    "Schedule" to "AW-17896876905/pPwlCI6KuusbEOnW8tVC"
)

private fun Map<String, Any?>.toJsObject(): dynamic {
    val obj = js("({})")
    forEach { (k, v) ->
        obj[k] = v
    }
    return obj
}
